import { useCallback, useEffect, useRef, useState } from "react";
import {
  acknowledgeDeviceWarning,
  markDeviceMonitorChecked,
  runDeviceAccessMonitor,
  shouldRunDeviceMonitor,
} from "../utils/deviceMonitor";
import { getAccessControlSettings } from "../utils/accessControl";

export function useDeviceAccessMonitor({ user, userDoc, loading }) {
  const runningRef = useRef(false);
  const [showWarning, setShowWarning] = useState(false);
  const [warningResult, setWarningResult] = useState(null);

  const checkNow = useCallback(
    async ({ reason = "manual", force = false } = {}) => {
      if (loading) return;
      if (!user?.uid) return;
      if (runningRef.current) return;

      try {
        runningRef.current = true;

        const settings = await getAccessControlSettings();
        if (!settings?.enableDeviceMonitoring) return;

        const intervalHours = Number(settings?.deviceMonitorIntervalHours || 6);

        if (!force && !shouldRunDeviceMonitor(user.uid, intervalHours)) return;

        const result = await runDeviceAccessMonitor({
          user,
          userDoc,
          reason,
        });

        markDeviceMonitorChecked(user.uid);

        if (result?.shouldShowWarning) {
          setWarningResult(result);
          setShowWarning(true);
        }
      } catch (e) {
        console.warn("useDeviceAccessMonitor failed:", e?.message);
      } finally {
        runningRef.current = false;
      }
    },
    [loading, user, userDoc]
  );

  useEffect(() => {
    checkNow({ reason: "app_loaded" });
  }, [checkNow]);

  useEffect(() => {
    const handleVisible = () => {
      if (document.visibilityState === "visible") {
        checkNow({ reason: "tab_visible" });
      }
    };

    window.addEventListener("focus", handleVisible);
    document.addEventListener("visibilitychange", handleVisible);

    return () => {
      window.removeEventListener("focus", handleVisible);
      document.removeEventListener("visibilitychange", handleVisible);
    };
  }, [checkNow]);

  const confirmWarning = useCallback(async () => {
    try {
      await acknowledgeDeviceWarning({
        user,
        result: warningResult,
      });
    } catch (e) {
      console.warn("acknowledgeDeviceWarning failed:", e?.message);
    } finally {
      setShowWarning(false);
      setWarningResult(null);
    }
  }, [user, warningResult]);

  return {
    showWarning,
    warningResult,
    confirmWarning,
    checkNow,
  };
}